import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'kk';

  const isEn = currentLang.startsWith('en');
  const isRu = currentLang.startsWith('ru');

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Kazakh%20East%20Asian%20Mongoloid%20Central%20Asian%20male%20soldiers%20athletes%20in%20military%20camouflage%20uniforms%20QAZAQSTAN%20patch%20on%20chest%20sprinting%20through%20outdoor%20sandy%20desert%20arena%20UAE%20Dubai%20competition%20ground%20low%20angle%20dramatic%20shot%20wide%20cinematic%20dust%20cloud%20golden%20warm%20sunset%20light%20dark%20amber%20graphite%20shadows%20disciplined%20formation%20powerful%20movement%20no%20text%20no%20flags&width=1920&height=1080&seq=hero-kz-v8&orientation=landscape&nocache=true"
          alt="Казахстанские спортсмены"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/65 via-background-950/40 to-background-950" />
        <div className="absolute inset-0 bg-background-950/25" />
      </div>

      {/* Tactical Grid Overlay */}
      <div className="absolute inset-0 tactical-grid opacity-30 pointer-events-none" />

      {/* Corner coordinates */}
      <div className="absolute top-24 left-4 md:left-8 coordinate">
        <span className="block">N42.32.18</span>
        <span className="block">E069.35.42</span>
      </div>
      <div className="absolute top-24 right-4 md:right-8 coordinate text-right">
        <span className="block">SECTOR 01</span>
        <span className="block">GRID A-7</span>
      </div>
      <div className="absolute bottom-24 left-4 md:left-8 coordinate">
        <span className="block">ALT 512M</span>
        <span className="block">T+00:00:00</span>
      </div>
      <div className="absolute bottom-24 right-4 md:right-8 coordinate text-right">
        <span className="block">SECURE</span>
        <span className="block">OP: READY</span>
      </div>

      {/* Crosshair center */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-16 bg-accent-500/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-[1px] bg-accent-500/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center pt-20">
        {/* Section badge (Clean, large, high-contrast) */}
        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-background-950/80 border border-accent-400/50 shadow-lg backdrop-blur-md mb-5 sm:mb-6">
          <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
          <span className="font-heading text-xs sm:text-sm md:text-base font-bold text-foreground-50 uppercase tracking-[0.2em]">
            {t('hero.label', isEn
              ? 'National Federation'
              : isRu
              ? 'Национальная Федерация'
              : 'Ұлттық Федерация')}
          </span>
        </div>

        {/* Headline (Stacked: top & bottom) */}
        <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tight leading-[0.92] mb-6 drop-shadow-lg">
          {isEn ? (
            <>
              <span className="block text-foreground-50">WARRIOR&apos;S</span>
              <span className="block text-accent-400 mt-1 sm:mt-2">WAY</span>
            </>
          ) : isRu ? (
            <>
              <span className="block text-foreground-50">ПУТЬ</span>
              <span className="block text-accent-400 mt-1 sm:mt-2">ВОИНА</span>
            </>
          ) : (
            <>
              <span className="block text-foreground-50">ЖАУЫНГЕР</span>
              <span className="block text-accent-400 mt-1 sm:mt-2">ЖОЛЫ</span>
            </>
          )}
        </h1>

        {/* Subtitle */}
        <p className="max-w-2xl mx-auto text-foreground-300 text-xs sm:text-sm md:text-base font-label uppercase tracking-wider leading-relaxed mb-8 sm:mb-10 px-2">
          {t('hero.subtitle', isEn
            ? '"NATIONAL FEDERATION OF MILITARY-APPLIED SPORTS" Public Organization'
            : isRu
            ? 'Общественная Организация «НАЦИОНАЛЬНАЯ ФЕДЕРАЦИЯ ПО ВОЕННО – ПРИКЛАДНЫМ ВИДАМ СПОРТА»'
            : '«Әскери-қолданбалы спорт түрлері бойынша Ұлттық Федерациясы» Қоғамдық Бірлестігі')}
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#about"
            className="inline-flex items-center gap-3 px-8 py-3.5 bg-primary-500 text-foreground-50 text-xs font-label tracking-widest uppercase hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap shadow-lg"
          >
            <span>{t('hero.btn_about', isEn ? 'About Federation' : isRu ? 'О Федерации' : 'Федерация туралы')}</span>
            <i className="ri-arrow-down-line" />
          </a>
          <a
            href="#events"
            className="inline-flex items-center gap-3 px-8 py-3.5 border border-foreground-700/40 text-foreground-300 text-xs font-label tracking-widest uppercase hover:border-accent-500 hover:text-accent-500 transition-colors duration-300 whitespace-nowrap bg-background-900/40 backdrop-blur-sm"
          >
            <span>{t('hero.btn_events', isEn ? 'Events' : isRu ? 'Мероприятия' : 'Іс-шаралар')}</span>
            <i className="ri-calendar-line" />
          </a>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent" />
    </section>
  );
}